import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Pagination,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { emailTemplateService } from './emailTemplate.service';
import type { EmailTemplate as EmailTemplateDto } from './emailTemplate.dto';

import { useToast } from '../../contexts/ToastContext';
const emailTemplateStyles = `
  body {
    margin: 0;
    padding: 20px;
    font-family: Arial, sans-serif;
    background-color: #f4f4f4;
  }
  .preview-container {
    max-width: 1200px;
    margin: 0 auto;
  }
  .preview-title {
    text-align: center;
    color: #121B31;
    margin-bottom: 30px;
  }
  .template-section {
    margin-bottom: 40px;
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  .template-name {
    color: #121B31;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
    border-bottom: 2px solid #D0E156;
    padding-bottom: 5px;
  }
  .email-container {
    max-width: 600px;
    margin: 0 auto;
    background-color: #ffffff;
    border: 1px solid #e5e7eb;
  }
  .header {
    background-color: #121B31;
    padding: 20px;
    text-align: center;
  }
  .logo {
    color: #ffffff;
    font-size: 24px;
    font-weight: bold;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
  .logo-image {
    height: 40px;
    width: auto;
    max-width: 200px;
  }
  .divider {
    height: 3px;
    background-color: #D0E156;
    margin: 0;
  }
  .content {
    padding: 30px;
  }
  .title {
    color: #121B31;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
    text-align: center;
  }
  .greeting {
    color: #374151;
    font-size: 16px;
    margin-bottom: 15px;
  }
  .message {
    color: #374151;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 15px;
  }
  .otp-container {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
  }
  .otp-code {
    font-size: 32px;
    font-weight: bold;
    color: #121B31;
    letter-spacing: 5px;
    margin: 10px 0;
  }
  .otp-label {
    color: #FFFFFF;
    font-size: 14px;
    margin-bottom: 10px;
  }
  .otp-note {
    color: #FFFFFF;
    font-size: 12px;
    margin-top: 10px;
  }
  .reset-button {
    display: inline-block;
    background-color: #121B31;
    color: #ffffff;
    padding: 15px 30px;
    text-decoration: none;
    border-radius: 8px;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
    transition: background-color 0.3s ease;
  }
  .reset-button:hover {
    background-color: #1e40af;
  }
  .reset-url {
    background-color: #f9fafb;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    margin: 20px 0;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
    color: #FFFFFF;
  }
  .reset-note {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-radius: 8px;
    padding: 15px;
    margin: 20px 0;
    color: #92400e;
    font-size: 14px;
  }
  .button-container {
    text-align: center;
    margin: 30px 0;
    color: #FFFFFF;
  }
  .footer {
    background-color: #ffffff;
    padding: 20px;
    text-align: center;
  }
  .contact-info {
    color: #121B31;
    font-size: 12px;
    line-height: 1.5;
  }
  .disclaimer {
    background-color: #121B31;
    color: #FFFFFF;
    padding: 15px;
    text-align: center;
    font-size: 12px;
  }
  .closing {
    color: #374151;
    font-size: 16px;
    margin-top: 20px;
  }
`;

const EmailTemplate = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateDto[]>([]);
  // No UI currently updates this - only the value is read to drive the
  // debounce effect below.
  const [searchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedContentId, setSelectedContentId] = useState<number | null>(
    null
  );

  const itemsPerPage = 10;

  useEffect(() => {
    emailTemplateService.setLogoutCallback(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    // console.log('🔍 Search term changed to:', searchTerm);
    const timer = setTimeout(() => {
      // console.log(
      //   '⏱️ Debounce complete - setting debouncedSearchTerm to:',
      //   searchTerm
      // );
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      // console.log('🧹 Clearing debounce timeout');
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    // console.log('📄 Debounced search term changed, resetting page to 1');
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchEmailTemplates = useCallback(async () => {
    if (!token) return;

    // console.log(
    //   '🔄 Fetching email templates - Page:',
    //   currentPage,
    //   'Search:',
    //   debouncedSearchTerm
    // );

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      };

      const response = await emailTemplateService.getEmailTemplates(
        token,
        params
      );

      if (response && response.data) {
        setEmailTemplates(response.data);
        setTotalPages(response.meta?.lastPage || 1);
      } else {
        setEmailTemplates([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setEmailTemplates([]);
      setTotalPages(1);
      showError(
        err instanceof Error ? err.message : 'Failed to fetch email templates'
      );
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm]);

  useEffect(() => {
    fetchEmailTemplates();
  }, [fetchEmailTemplates]);

  const handleDelete = async () => {
    if (!token || !selectedContentId) return;

    try {
      await emailTemplateService.deleteEmailTemplate(token, selectedContentId);
      showSuccess('Email template deleted successfully!');
      fetchEmailTemplates();
    } catch (err) {
      showError(
        err instanceof Error ? err.message : 'Failed to delete email template'
      );
    }
    setDeleteDialog(false);
    setSelectedContentId(null);
  };

  const handleEdit = (id: number) => {
    navigate(`/email-templates/edit/${id}`);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const plainText = (html: string) =>
    html
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  return (
    <Box>
      <style>{emailTemplateStyles}</style>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Email Templates</Typography>
      </Box>

      {/* Email Templates Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : emailTemplates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No email templates found
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Email Type</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>From Email</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailTemplates.map((template, index) => (
                  <TableRow key={template.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {(currentPage - 1) * 10 + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {template.emailType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.fromEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {plainText(template.content).slice(0, 80) || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(template.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {!loading && totalPages > 1 ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="caption" color="text.secondary">
                {loading
                  ? 'Loading...'
                  : `Showing page ${currentPage} of ${totalPages}`}
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Content</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this content? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplate;
